/**
 * Session cache for UAT Operations Solutions
 * 
 * This cache persists for the browser session and is shared across
 * all components that fetch operations solutions data.
 */

// Cache key format: `${environment}-${productId}-${date}-${timezone}`
const cache = new Map<string, any>();

/**
 * Get cached operations solutions
 * 
 * @param environment - Environment (UAT or Production)
 * @param productId - Product/Network ID
 * @param date - Service date in YYYY-MM-DD format
 * @param timezone - Timezone string
 * @returns Cached response or null if not found
 */
export function getCachedOperationsSolutions(
  environment: string,
  productId: string,
  date: string,
  timezone: string
): any | null {
  const cacheKey = `${environment}-${productId}-${date}-${timezone}`;
  return cache.get(cacheKey) || null;
}

/**
 * Set cached operations solutions
 * 
 * @param environment - Environment (UAT or Production)
 * @param productId - Product/Network ID
 * @param date - Service date in YYYY-MM-DD format
 * @param timezone - Timezone string
 * @param response - Operations solutions response to cache
 */
export function setCachedOperationsSolutions(
  environment: string,
  productId: string,
  date: string,
  timezone: string,
  response: any
): void {
  const cacheKey = `${environment}-${productId}-${date}-${timezone}`;
  cache.set(cacheKey, response);
}

/**
 * Clear all cached operations solutions
 */
export function clearOperationsSolutionsCache(): void {
  cache.clear();
}

/**
 * Clear cached operations solutions for a specific environment
 * 
 * @param environment - Environment to clear cache for
 */
export function clearOperationsSolutionsCacheForEnvironment(environment: string): void {
  const keysToDelete: string[] = [];
  cache.forEach((_, key) => {
    if (key.startsWith(`${environment}-`)) {
      keysToDelete.push(key);
    }
  });
  keysToDelete.forEach(key => cache.delete(key));
}

